import React, { useCallback } from 'react';
import { useOverlay } from '../contexts/OverlayContext';
import { usePointerHover } from './usePointerHover';
import SpellDetails, { SpellDetailsOverlayHeader } from '../components/spells/SpellDetails';

const TXT_DETAILS = {
    fr: 'Voir les détails',
    en: 'View details',
    de: 'Details anzeigen',
    es: 'Ver detalles',
    pt: 'Ver detalhes',
};

const TXT_KIND = {
    spell: { fr: 'Sort', en: 'Spell', de: 'Zauber', es: 'Hechizo', pt: 'Feitiço' },
    weapon: { fr: 'Arme', en: 'Weapon', de: 'Waffe', es: 'Arma', pt: 'Arma' },
};

// Same portrait SpellView puts on the card: summons show the monster, not the
// spell glyph.
const portraitOf = (entity) => (
    Array.isArray(entity?.summonChildRows) && entity.summonChildRows.length
        ? (entity.summonMonsterIconUrl || entity.icon)
        : entity?.icon
);

/**
 * Touch counterpart of the desktop spell hover tooltip. Phones never get the
 * tooltip (Tooltip is hover-gated), so the same SpellDetails panel is reached
 * by tapping the spell icon and opens as an overlay instead.
 *
 * Returns null where the pointer can hover, so the icon stays inert on desktop
 * and the hover tooltip remains the only way in.
 */
export function useSpellDetailsOverlay(spell, labels, lang = 'fr', kind = 'spell') {
    const hasPointerHover = usePointerHover();
    const { showOverlay } = useOverlay();

    const openDetails = useCallback(() => {
        if (!spell) return;
        const kindLabel = TXT_KIND[kind] || TXT_KIND.spell;
        showOverlay(
            [
                <SpellDetailsOverlayHeader
                    key="spell-details-hdr"
                    icon={portraitOf(spell)}
                    title={kindLabel[lang] || kindLabel.en}
                />,
                <SpellDetails key="spell-details" spell={spell} labels={labels} lang={lang} embedded />,
            ],
            { compact: true }
        );
    }, [spell, labels, lang, kind, showOverlay]);

    return {
        onIconActivate: hasPointerHover || !spell ? null : openDetails,
        iconActivateLabel: TXT_DETAILS[lang] || TXT_DETAILS.en,
    };
}

export default useSpellDetailsOverlay;
